import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Select, { SelectRenderer } from "react-dropdown-select";
import { usersByGroupListName } from "src/api/config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import UsersTableWithFilters from "src/components/tables/notificationTables/UsersTableWithFilters";
import UsersTableWithFiltersMobile from "src/components/tables/notificationTables/UsersTableWithFilters.mobile";
import { SearchUI } from "src/components/ui/Search";
import TGButton from "src/components/ui/TGButton";
import TGCheckbox from "src/components/ui/TGCheckbox";
import TGRadio from "src/components/ui/TGRadio";
import {
  Option,
  Props,
  TGSelectPicker,
  TGSelectUserPickerByProperties,
} from "src/components/ui/TGSelect";
import TGTag from "src/components/ui/TGTag";
import { UserAvatars } from "src/components/ui/TGUserAvatarsList";
import { searchPeopleRequest } from "src/components/ui/form/peoplePicker/api";
import { getDeclension } from "src/helpers/formatBytes";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { getItemsRequest } from "src/modules/spItems/api";
import { selectCurrentUser } from "src/modules/users/selector";
import { SearchPeopleType } from "src/types/main";
import { buildSearchQuery } from "src/utils/api.utils";
import { chunkArray, getName } from "src/utils/array.utils";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import {
  getNameAndSurname,
  nameFromEmail,
  prefix,
  removePrefix,
  shortNameFromEmail,
} from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { SelectIcon } from "./SelectIcon";
import {
  removeInvisibleChar,
  parseDelimitedString,
} from "src/utils/string.utils";
import { strapiDefaultUrl, strapiUrl } from "src/modules/users/constants";
import { resolveResourceUrl } from "src/utils/url.utils";
import axios from "axios";
import { isStrapi } from "src/api/config/systemSwitch";
import Loading from "src/components/Loading";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { isPWA } from "src/utils/mobile";

interface SelectEmployeesContextType {
  items: Map<number, NotifyMyEmployeesType>;
  addItem: (item: NotifyMyEmployeesType) => void;
  removeItem: (id: number) => void;
  addItems: (items: NotifyMyEmployeesType[]) => void;
  removeItems: (data: NotifyMyEmployeesType[]) => void;
  count: number;
  clearItems: () => void;
}

const SelectEmployeesContext = createContext<SelectEmployeesContextType | null>(
  null
);

export const useSelectEmployeesContext = (): SelectEmployeesContextType => {
  const context = useContext(SelectEmployeesContext);

  if (!context) {
    throw new Error(
      "useSelectEmployeesContext должен использоваться внутри SelectEmployeesProvider"
    );
  }

  return context;
};

interface SelectEmployeesProviderProps {
  initial: Map<number, NotifyMyEmployeesType>;
  children: ReactNode;
}

const SelectEmployeesProvider: React.FC<SelectEmployeesProviderProps> = ({
  initial,
  children,
}) => {
  const [items] = useState<Map<number, NotifyMyEmployeesType>>(initial);

  const [count, setCount] = useState<number>(0);

  const addItem = (item: NotifyMyEmployeesType) => {
    items.set(item.id, item);

    setCount(items.size);
  };

  const addItems = (data: NotifyMyEmployeesType[]) => {
    data.forEach((d) => items.set(d.id, d));

    setCount(items.size);
  };

  const removeItem = (id: number) => {
    if (items.has(id)) {
      items.delete(id);
    }

    setCount(items.size);
  };

  const removeItems = (data: NotifyMyEmployeesType[]) => {
    if (!data.length) items.clear();

    data.forEach((d) => items.delete(d.id));

    setCount(items.size);
  };

  const clearItems = () => {
    setCount(0);
    items.clear();
  };

  const contextValue: SelectEmployeesContextType = {
    items,
    addItem,
    removeItem,
    addItems,
    removeItems,
    count,
    clearItems,
  };

  return (
    <SelectEmployeesContext.Provider value={contextValue}>
      {children}
    </SelectEmployeesContext.Provider>
  );
};

function SelecEmployeesWrapper({
  title,
  submit,
  read = false,
  readInitial = [],
  zIndex,
  isMobileView = false,
}: {
  title: string;
  submit: (items: Map<number, NotifyMyEmployeesType>) => void;
  read?: boolean;
  readInitial?: NotifyMyEmployeesType[];
  zIndex?: number;
  isMobileView?: boolean;
}) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [employees, setEmployees] =
    useState<NotifyMyEmployeesType[]>(readInitial);
  const { items, count, clearItems, addItems } = useSelectEmployeesContext();

  const isEmpty = !count;

  function toggleDrawer(state: boolean) {
    setOpenDrawer(state);
  }

  const [openConfirmEmployeeDialog, setOpenConfirmEmployeeDialog] =
    useState(false);

  function BackButton() {
    clearItems();
    addItems(employees);

    toggleDrawer(false);
  }

  function submitEmployees() {
    const temp = [...items.values()];
    submit(items);
    setEmployees(temp);
    setOpenDrawer(false);
  }

  return (
    <FlexRow
      className={twMerge(
        "flex-nowrap justify-between items-center",
        isMobileView ? "p-5" : "p-9",
      )}
    >
      <FlexCol className="grow">
        <FlexRow
          className={twMerge(
            isEmpty ? "" : "text-black",
            "gap-x-5 justify-between items-center font-medium"
          )}
        >
          <FlexCol className="mr-5 grow">
            {isMobileView ? (
              <FlexCol className="gap-y-1">
                <FlexCol
                  className={twMerge(
                    count > 100 ? "text-[#BA0C2F]" : "",
                    "text-[18px] leading-[28px] shrink-0",
                  )}
                >
                  <span>{title}:</span>
                  <span className="block">
                    {isEmpty
                      ? title === "Сотрудники"
                        ? "не выбраны"
                        : "сотрудники не выбраны"
                      : "выбрано " + count}
                  </span>
                </FlexCol>
                <FlexCol className="w-full break-words">
                  <UserAvatars users={employees.map((emp) => emp.user)} />
                </FlexCol>
              </FlexCol>
            ) : (
              <FlexRow className="space-x-5">
                <FlexCol
                  className={twMerge(
                    count > 100 ? "text-[#BA0C2F]" : "",
                    "text-[18px] shrink-0",
                  )}
                >
                  {title}:{" "}
                  {isEmpty
                    ? title === "Сотрудники"
                      ? "не выбраны"
                      : "сотрудники не выбраны"
                    : "выбрано " + count}
                </FlexCol>
                <FlexCol className="w-full break-words">
                  <UserAvatars users={employees.map((emp) => emp.user)} />
                </FlexCol>
              </FlexRow>
            )}
          </FlexCol>
          {!read && (
            <FlexCol className="shrink-0">
              <button onClick={() => toggleDrawer(true)}>
                <SelectIcon isSelect={!isEmpty} />
              </button>
            </FlexCol>
          )}
        </FlexRow>
      </FlexCol>
      {!read && (
        <DrawerComponent
          zIndex={zIndex}
          percentage="87"
          open={openDrawer}
          toggleDrawer={toggleDrawer}
        >
          {isMobileView ? (
            <>
              <header className="border-b px-5 pt-5 pb-4 text-black">
                <h1>Выберите сотрудников</h1>
                <div
                  className={twMerge(
                    "mt-2 text-[16px] leading-[24px]",
                    count > 100 ? "text-[#BA0C2F]" : "text-[#637381]",
                  )}
                >
                  Выбрано: {count}
                </div>
              </header>
              <FlexCol className="max-h-[calc(100dvh-206px)] overflow-y-auto">
                <ContentArea isMobileView={true} />
              </FlexCol>
              <footer className="flex flex-col gap-3 border-t p-5 text-black shrink-0">
                <TGButton
                  disabled={!count}
                  color="bluedark"
                  className="h-[44px] w-full justify-center text-[14px]"
                  onClick={() =>
                    count > 100
                      ? setOpenConfirmEmployeeDialog(true)
                      : submitEmployees()
                  }
                >
                  Выбрать сотрудника
                </TGButton>
                <TGButton
                  onClick={BackButton}
                  className="h-[44px] w-full justify-center text-[14px]"
                >
                  Отмена
                </TGButton>
              </footer>
            </>
          ) : (
            <>
              <header className="flex flex-row items-center px-10 pt-10 pb-8 space-x-5 text-black border-b">
                <FlexCol>
                  <h1>Выберите сотрудников</h1>
                </FlexCol>
                <FlexCol>
                  <TGTag
                    className={twMerge(
                      count > 0 ? "text-black" : "text-[#a0a3a9]",
                      "translate-y-1"
                    )}
                    color={count > 100 ? "red" : "gray"}
                  >
                    Выбрано: {count}
                  </TGTag>
                </FlexCol>
              </header>
              <FlexRow className="overflow-auto max-h-[765px]">
                <ContentArea />
              </FlexRow>
              <footer className="flex flex-row justify-between items-center p-10 text-black border-t shrink-0">
                <TGButton onClick={BackButton}>Назад</TGButton>
                <TGButton
                  disabled={!count}
                  color="bluedark"
                  onClick={() =>
                    count > 100
                      ? setOpenConfirmEmployeeDialog(true)
                      : submitEmployees()
                  }
                >
                  Выбрать сотрудников
                </TGButton>
              </footer>
            </>
          )}
        </DrawerComponent>
      )}
      <Modal
        zIndex={70}
        open={openConfirmEmployeeDialog}
        toggleDialog={(state) => setOpenConfirmEmployeeDialog(state)}
        modalClassName={twMerge(
          "min-h-0",
          isMobileView && "!min-h-px !w-[calc(100vw-24px)] !min-w-0",
        )}
      >
        <header className={twMerge(isMobileView ? "px-5 pb-2" : "px-10 pb-4")}>
          <h2>Подтвердите действие</h2>
        </header>

        <div className={twMerge(isMobileView ? "px-5 py-4 grow" : "px-10 py-5 grow")}>
          Вы уверены, что хотите добавить {count}{" "}
          {getDeclension("сотрудник", count)}?
        </div>

        <footer
          className={twMerge(
            isMobileView
              ? "flex flex-col gap-3 px-5 py-4 border-t"
              : "flex justify-between px-10 pt-10 mt-4 border-t",
          )}
        >
          <TGButton
            onClick={() => setOpenConfirmEmployeeDialog(false)}
            className={twMerge(
              isMobileView && "h-[44px] w-full justify-center text-[14px]",
            )}
          >
            Отмена
          </TGButton>
          <TGButton
            color="bluedark"
            className={twMerge(
              isMobileView && "h-[44px] w-full justify-center text-[14px]",
            )}
            onClick={() => {
              setOpenConfirmEmployeeDialog(false);
              submitEmployees();
            }}
          >
            Да, добавить всех сотрудников
          </TGButton>
        </footer>
      </Modal>
    </FlexRow>
  );
}

function SelectEmployees({
  onResult,
  title,
  read,
  readInitial,
  zIndex,
  isMobileView = false,
}: {
  initial?: Map<number, NotifyMyEmployeesType>;
  onResult: (items: Map<number, NotifyMyEmployeesType>) => void;
  title: string;
  read?: boolean;
  readInitial?: NotifyMyEmployeesType[];
  zIndex?: number;
  isMobileView?: boolean;
}) {
  let initial = new Map<number, NotifyMyEmployeesType>();

  return (
    <SelectEmployeesProvider initial={initial}>
      <SelecEmployeesWrapper
        zIndex={zIndex}
        title={title}
        submit={onResult}
        read={read}
        readInitial={readInitial}
        isMobileView={isMobileView}
      />
    </SelectEmployeesProvider>
  );
}

export default SelectEmployees;

export type NotifyMyEmployeesType = {
  id: number;
  user: {
    avatar: string | null;
    name: string;
    email: string;
    id: number;
    personalNumber: string;
    shortName: string;
  };
  location: string;
  department: string;
  position: string;
};

function ContentArea({ isMobileView = false }: { isMobileView?: boolean }) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { items } = useSelectEmployeesContext();
  const [employees, setEmployees] = useState<NotifyMyEmployeesType[]>([]);
  const [positions, setPositions] = useState<Option[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [subordinates, setSubordinates] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [groupPeople, setGroupPeople] = useState<Option[]>([]);

  const [query, setQuery] = useState("");

  function handleSearch(value: string) {
    setQuery(value);
  }

  useAsyncEffect(async () => {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (
      !query &&
      departments.length === 0 &&
      positions.length === 0 &&
      locations.length === 0 &&
      groupPeople.length === 0 &&
      subordinates.length === 0
    ) {
      setEmployees([...items.values()]);
      return;
    }

    if (groupPeople.length) {
      const flatGroupPeople = groupPeople
        .flatMap((group) => group.value)
        .map((email: string) =>
          removeInvisibleChar(removePrefix(email, prefix))
        );

      const chunksPeople = chunkArray(flatGroupPeople, 25);

      const chunksPromisePeople = chunksPeople.map((chunkPeople) => {
        let searchQuery = buildSearchQuery(
          query,
          departments.map(getName),
          positions.map(getName),
          locations.map(getName),
          chunkPeople,
          subordinates
        );

        return axios.get(`${strapiUrl}custom-users?${searchQuery}`, {
          headers,
          withCredentials: true,
        });
      });

      try {
        const allResponses = await Promise.all(chunksPromisePeople);

        const employeesData: NotifyMyEmployeesType[] = [];

        if (isStrapi) {
          for (const response of allResponses) {
            if (
              "data" in response &&
              response.data &&
              response.data.data.length > 0
            ) {
              const employeesDataModified: NotifyMyEmployeesType[] =
                response.data.data.map((user: any) => {
                  const email = user.email;

                  const name = user.Name
                    ? getNameAndSurname(user.Name, email)
                    : nameFromEmail(email);
                  const shortName = user.Name
                    ? getNameAndSurname(user.Name, email, true)
                    : shortNameFromEmail(email);

                  return {
                    id: user.id,
                    department: user.Department || "----",
                    position: user.Position || "----",
                    location: user.Location || "----",
                    user: {
                      avatar:
                        (user.PictureUrl &&
                          resolveResourceUrl(user.PictureUrl.url, strapiDefaultUrl)) ||
                        null,
                      name: name,
                      email: email,
                      id: user.id,
                      personalNumber: user.PersonalNumber,
                      shortName: shortName,
                    },
                  };
                });
              employeesData.push(...employeesDataModified);
            }
          }
        } else {
          for (const response of allResponses) {
            const sharepointResponse =
              "get_value" in response && typeof response.get_value === "function"
                ? (response.get_value() as
                    | { ResultTables: { ResultRows: any[] }[] }
                    | null)
                : null;

            if (sharepointResponse && sharepointResponse.ResultTables.length > 0) {
              const employeesDataModified: NotifyMyEmployeesType[] = sharepointResponse.ResultTables[0].ResultRows.map((user: any) => {
                  const email = removePrefix(user.AccountName, prefix);

                  const name = user.PreferredName
                    ? getNameAndSurname(user.PreferredName, email)
                    : nameFromEmail(email);
                  const shortName = user.PreferredName
                    ? getNameAndSurname(user.PreferredName, email, true)
                    : shortNameFromEmail(email);

                  return {
                    id: user.DocId,
                    department: user.DepartmentRUS || user.Department || "----",
                    position: user.TitleRUS || user.JobTitle || "----",
                    location:
                      user["SPS-Location"] || user.BaseOfficeLocation || "----",
                    user: {
                      avatar: user.PictureURL,
                      name: name,
                      email: email,
                      id: user.DocId,
                      personalNumber: user.apiGlobalID || user.UserName,
                      shortName: shortName,
                    },
                  };
                });
              employeesData.push(...employeesDataModified);
            }
          }
        }
        if (employeesData.length) {
          setEmployees(employeesData);
        } else {
          setEmployees([...items.values()]);
        }
      } catch (error) {
        console.error("chunksPromisePeople", error);
      }
    } else {
      let searchQuery = buildSearchQuery(
        query,
        departments.map(getName),
        positions.map(getName),
        locations.map(getName),
        [],
        subordinates
      );
      let employeesDataModified: NotifyMyEmployeesType[] = [];
      if (isStrapi) {
        const response = await axios.get(
          `${strapiUrl}custom-users?${searchQuery}`,
          {
            headers,
            withCredentials: true,
          }
        );
        if (response.data.data.length > 0) {
          employeesDataModified = response.data.data.map((user: any) => {
            const email = user.email;

            const name = user.Name
              ? getNameAndSurname(user.Name, email)
              : nameFromEmail(email);
            const shortName = user.Name
              ? getNameAndSurname(user.Name, email, true)
              : shortNameFromEmail(email);

            return {
              id: user.id,
              department: user.Department || "----",
              position: user.Position || "----",
              location: user.Location || "----",
              user: {
                avatar:
                  (user.PictureUrl && resolveResourceUrl(user.PictureUrl.url, strapiDefaultUrl)) ||
                  null,
                name: name,
                email: email,
                id: user.id,
                personalNumber: user.PersonalNumber,
                shortName: shortName,
              },
            };
          });
        }
      }
      if (employeesDataModified.length > 0) {
        setEmployees(employeesDataModified);
      } else {
        setEmployees([...items.values()]);
      }
    }
  }, [query, positions, locations, departments, groupPeople, subordinates]);

  return (
    <FlexCol className="grow">
      <FlexRow className={twMerge(isMobileView ? "px-5 py-4" : "px-7.5 py-6")}>
        <FlexCol className="w-full">
          <div
            className={twMerge(
              "custom-selects mb-5",
              isMobileView ? "grid grid-cols-1 gap-4" : "grid grid-cols-2 gap-5",
            )}
          >
            <div>
              <TGSelectPicker
                values={locations}
                onChange={(values) => setLocations(values)}
                fetchKey={["SPS-Location", "Region", "BaseOfficeLocation"]}
                classNames={{ selectClassName: "!px-5" }}
                searchPlaceholder="Найти локацию"
                dataField={{
                  placeholder: "Выберите один или несколько вариантов",
                  type: "peoplePicker",
                  require: false,
                  title: "Локация",
                  width: "w-full",
                  key: "peoplePicker",
                  single: false,
                }}
                errors={{}}
              />
            </div>
            <div className="overflow-initial">
              <GroupSelect
                values={groupPeople}
                onChange={(values) => setGroupPeople(values)}
                fetchKey="Groups"
                classNames={{ selectClassName: "!px-5" }}
                searchPlaceholder="Найти группу"
                dataField={{
                  type: "peoplePicker",
                  require: false,
                  title: isMobileView ? "Группа сотрудников" : "Группы сотрудников",
                  width: "w-full",
                  key: "peoplePicker",
                  single: false,
                }}
                min={3}
                errors={{}}
              />
            </div>
            <div>
              <TGSelectPicker
                values={departments}
                onChange={(values) => setDepartments(values)}
                fetchKey={["DepartmentRUS", "Department"]}
                classNames={{ selectClassName: "!px-5" }}
                searchPlaceholder="Найти отдел"
                dataField={{
                  placeholder: "Выберите один или несколько вариантов",
                  type: "peoplePicker",
                  require: false,
                  title: "Отдел",
                  width: "w-full",
                  key: "peoplePicker",
                  single: false,
                }}
                errors={{}}
              />
            </div>
            <div>
              <TGSelectPicker
                values={positions}
                onChange={(values) => setPositions(values)}
                fetchKey={["TitleRUS", "JobTitleRus", "JobTitle"]}
                classNames={{ selectClassName: "!px-5" }}
                searchPlaceholder="Найти должность"
                dataField={{
                  placeholder: "Выберите один или несколько вариантов",
                  type: "peoplePicker",
                  require: false,
                  title: "Должность",
                  width: "w-full",
                  key: "peoplePicker",
                  single: false,
                }}
                errors={{}}
              />
            </div>
            <div>
              <TGSelectUserPickerByProperties
                values={subordinates}
                onChange={(values) => setSubordinates(values)}
                fetchKey="ManagerFullNameRUS"
                classNames={{ selectClassName: "!px-5" }}
                searchPlaceholder="Найти по имени сотрудника"
                dataField={{
                  placeholder: "Выберите один или несколько вариантов",
                  type: "peoplePicker",
                  require: false,
                  title: "ФИО Руководителя",
                  width: "w-full",
                  key: "peoplePicker",
                  single: false,
                }}
                errors={{}}
              />
            </div>
          </div>
          <FlexRow className="flex-wrap mb-5">
            <SearchUI
              ref={searchInputRef}
              placeholder="Введите имя сотрудника или табельный номер"
              defaultValue={query}
              onClick={handleSearch}
              label={"ФИО"}
              labelClass="text-black"
            />
            <span className="text-[14px] text-[#4B5563] mt-1.5">
              Найдено сотрудников: {employees.length}
            </span>
          </FlexRow>

          {/* <FlexRow className="flex-wrap mb-5">
            <SearchUI
              ref={searchSubsInputRef}
              placeholder="Введите имя сотрудника или табельный номер"
              defaultValue={subsQuery}
              onClick={handleSubsSearch}
              label={"ФИО Руководителя"}
              labelClass="text-black"
            />
            <span className="text-[14px] text-[#4B5563] mt-1.5">Найдено сотрудников: {employees.length}</span>
          </FlexRow> */}
          {isMobileView ? (
            <UsersTableWithFiltersMobile items={employees} />
          ) : (
            <UsersTableWithFilters items={employees} />
          )}
        </FlexCol>
      </FlexRow>
    </FlexCol>
  );
}

export function GroupSelect(props: Props) {
  const {
    dataField,
    onChange,
    values,
    errors,
    fetchKey,
    searchPlaceholder = "",
    min = 1,
    classNames,
  } = props;

  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [checkGroupWithMe, setCheckGroupWithMe] = useState(false);
  const [searchType, setSearchType] = useState("group");
  const currentUser = useAppSelector((state) => selectCurrentUser(state));

  function changeSearchType(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchType(event.target.name);
    setSearchString("");
    setOptions([]);
    onChange([]);
  }

  function changeGroupState() {
    setCheckGroupWithMe(!checkGroupWithMe);
  }

  function changeSearchQuery(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchString(event.target.value);
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(false);
      try {
        if (
          (searchString.length >= min || checkGroupWithMe) &&
          searchType === "group"
        ) {
          const filters: Record<string, any> = {};
          if (searchString.length >= min) {
            filters.Title = searchString + "*";
          }

          if (checkGroupWithMe && currentUser) {
            filters.Users = [currentUser.email];
          }

          const { items } = await getItemsRequest({
            listName: usersByGroupListName,
            filters: filters,
            rowLimit: 100,
          });

          if (items) {
            const mappedItemsByOptionType = items.map((item) => ({
              id: item.ID.toString(),
              name: item.Title,
              value: parseDelimitedString(item.Users),
            }));
            setOptions(mappedItemsByOptionType);
          }
        } else if (
          (searchString.length >= min || checkGroupWithMe) &&
          searchType === "person"
        ) {
          if (searchString.length >= min) {
            const items = (await searchPeopleRequest(
              searchString
            )) as unknown as SearchPeopleType[];
            const mappedItemsByOptionType = items.map(
              (item) =>
                item.AccountName.split("|")[
                  item.AccountName.split("|").length - 1
                ]
            );

            const filteredGroupByUsers = await getItemsRequest({
              listName: usersByGroupListName,
              filters: { Users: mappedItemsByOptionType },
              rowLimit: 100,
            });

            if (filteredGroupByUsers.items.length > 0) {
              if (filteredGroupByUsers.items) {
                const mappedItemsByOptionType = filteredGroupByUsers.items.map(
                  (item) => ({
                    id: item.ID.toString(),
                    name: item.Title,
                    value: parseDelimitedString(item.Users),
                  })
                );
                setOptions(mappedItemsByOptionType);
              }
            }
          }
        } else {
          if (searchString.length === 0) setOptions([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [searchString, checkGroupWithMe, searchType]);

  const customContentRenderer = ({
    props,
    state,
    methods,
  }: SelectRenderer<Option>) => {
    return (
      <>
        {(state.values.length > 0 &&
          state.values.map((option) => (
            <span
              key={option.id}
              className="rounded p-1 flex flex-row items-center mr-2.5 bg-[#F3F4F6]"
            >
              <span className="block mx-2.5">{option.name}</span>
              {!dataField.single && (
                <span onClick={(e) => methods.removeItem(e, option, true)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                </span>
              )}
            </span>
          ))) || (
          <input
            placeholder={
              searchType === "group"
                ? "Найти по названию группы"
                : "Найти по имени сотрудника"
            }
            className="w-full h-full pl-0 border-none focus-visible:border-none focus-visible:ring-0 placeholder:text-[#9CA3AF]"
            onChange={changeSearchQuery}
            value={searchString}
          />
        )}
      </>
    );
  };

  const customDropdownHandleRenderer = ({
    props,
    state,
    methods,
  }: SelectRenderer<Option>) => {
    return (
      <ChevronDownIcon
        className={`${
          state.dropdown ? "rotate-180 transform" : ""
        } h-5 w-5 text-black`}
      />
    );
  };

  const customDropdownRenderer = ({
    props,
    state,
    methods,
  }: SelectRenderer<Option>) => {
    return (
      <div className="relative py-2.5 bg-white shadow-custom">
        <div className="relative px-5 pt-2.5 pb-5 bg-white border-b">
          <TGRadio
            name="group"
            onChange={changeSearchType}
            isChecked={searchType === "group"}
          >
            Поиск по названию группы
          </TGRadio>
          <TGRadio
            name="person"
            onChange={changeSearchType}
            isChecked={searchType === "person"}
          >
            Поиск по ФИО участника группы
          </TGRadio>
        </div>

        <div className="relative px-5 pt-2.5 pb-5 bg-white border-b">
          <TGCheckbox onChange={changeGroupState} isChecked={checkGroupWithMe}>
            Только те группы, в которых я состою
          </TGCheckbox>
        </div>
        {/* ТУТ */}
        <div className="relative px-5">
          {currentUser &&
            currentUser.reports &&
            currentUser.reports.length > 0 && (
              <FlexRow
                className="space-x-2.5 py-2"
                onClick={() =>
                  methods.addItem({
                    id: "directReports",
                    name: "Мои подчиненные",
                    value: currentUser.reports,
                  })
                }
              >
                <FlexCol className="w-3/12 shrink-0">Мои подчиненные</FlexCol>
                <FlexCol className="w-full">
                  <UserAvatars
                    users={currentUser.reports.map((email: string) => {
                      let emailString = removeInvisibleChar(
                        removePrefix(email, prefix)
                      )
                        .toLowerCase()
                        .trim();

                      return {
                        email: emailString,
                        name: nameFromEmail(emailString),
                        shortName: shortNameFromEmail(emailString),
                        avatar: null,
                      };
                    })}
                  />
                </FlexCol>
              </FlexRow>
            )}
          {currentUser &&
            currentUser.peers &&
            currentUser.peers.results &&
            currentUser.peers.results.length > 0 && (
              <FlexRow
                className="space-x-2.5 py-2"
                onClick={() =>
                  methods.addItem({
                    id: "peers",
                    name: "Мои коллеги",
                    value: currentUser.peers.results,
                  })
                }
              >
                <FlexCol className="w-3/12 shrink-0">Мои коллеги</FlexCol>
                <FlexCol className="w-full">
                  <UserAvatars
                    users={currentUser.peers.results.map((email: string) => ({
                      email: removeInvisibleChar(removePrefix(email, prefix)),
                      name: nameFromEmail(email),
                      shortName: shortNameFromEmail(email),
                      avatar: null,
                    }))}
                  />
                </FlexCol>
              </FlexRow>
            )}
          {currentUser &&
            currentUser.managers &&
            currentUser.managers.results &&
            currentUser.managers.results.length > 0 && (
              <FlexRow
                className="space-x-2.5 py-2"
                onClick={() =>
                  methods.addItem({
                    id: "managers",
                    name: "Мои руководители",
                    value: currentUser.managers.results,
                  })
                }
              >
                <FlexCol className="w-3/12 shrink-0">Мои руководители</FlexCol>
                <FlexCol className="w-full">
                  <UserAvatars
                    users={currentUser.managers.results.map(
                      (email: string) => ({
                        email: removeInvisibleChar(removePrefix(email, prefix)),
                        name: nameFromEmail(email),
                        shortName: shortNameFromEmail(email),
                        avatar: null,
                      })
                    )}
                  />
                </FlexCol>
              </FlexRow>
            )}
        </div>

        <div className="px-5">
          {/* {!error ? ( */}
          {state.search.length > min && props.options.length === 0 && (
            <span>
              Нет совпадений по запросу <strong>{state.search}</strong>
            </span>
          )}
          {/* ) : (
             <span>Введен некорректны(й|е) символ(ы)</span>
           ) */}
        </div>

        {props.options.length > 0 ? (
          <div className="px-5 overflow-auto border-t max-h-80 pt-[25px]">
            {props.options.map((option) => {
              if (!props.keepSelectedInList && methods.isSelected(option)) {
                return null;
              }

              return (
                <div className="py-2" key={option.id}>
                  {option.disabled ? (
                    <div aria-disabled>{option.name}</div>
                  ) : (
                    <FlexRow
                      className="space-x-2.5 py-2"
                      onClick={() => methods.addItem(option)}
                    >
                      <FlexCol className="w-3/12 shrink-0">
                        {option.name}
                      </FlexCol>
                      <FlexCol className="break-words grow">
                        <UserAvatars
                          users={option.value.map((email: string) => {
                            return {
                              email: removeInvisibleChar(
                                removeInvisibleChar(removePrefix(email, prefix))
                              ),
                              name: nameFromEmail(email),
                              shortName: shortNameFromEmail(email),
                              avatar: null,
                            };
                          })}
                        />
                      </FlexCol>
                    </FlexRow>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={`${dataField.width} mb-5 ${
        (dataField.disabled && "cursor-not-allowed") || ""
      }`}
    >
      <label className="flex flex-row justify-between text-sm font-medium leading-6 text-gray-900 mb-2.5">
        <span>{dataField.title}</span>
        {dataField.require && (
          <div className="flex flex-row justify-between">
            {Object.keys(errors).length > 0 && errors[dataField.key] && (
              <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
            )}
            <span className="text-[#BA0C2F]">*</span>
          </div>
        )}
      </label>
      <Select<Option>
        values={values}
        multi={true}
        searchable={true}
        className={classNames?.selectClassName || ""}
        contentRenderer={customContentRenderer}
        dropdownRenderer={customDropdownRenderer}
        dropdownHandleRenderer={customDropdownHandleRenderer}
        options={options}
        disabled={dataField.disabled}
        onChange={(value) => onChange(value)}
        labelField="name"
        valueField="id"
        noDataLabel="Нет данных"
      />
      {dataField.notice && (
        <div className="text-sm text-[#6B7280]">{dataField.notice}</div>
      )}
    </div>
  );
}

export function MyListbox<T extends { ID: number; Title: string }>({
  value,
  onChange,
  list,
  label,
  labelClass,
  className,
  multi = true,
  required = false,
  disable = false,
  placeholder = "Выберите один или несколько вариантов",
  loading = false,
}: {
  value: T[];
  onChange: (items: T[]) => void;
  list: T[];
  label?: string;
  labelClass?: string;
  className?: string;
  multi?: boolean;
  required?: boolean;
  disable?: boolean;
  placeholder?: string;
  loading?: boolean;
}) {
  const dropdownRenderer = ({ props, state, methods }: SelectRenderer<any>) => {
    return (
      <div className="relative rounded-md max-h-80 scrollbar-thumb-rounded scrollbar-thin scrollbar-w-2 scrollbar-thumb-[#dfe4ea] scrollbar-track-inherit">
        {props.options.map((option) => {
          return (
            <button
              className={twMerge(
                methods.isSelected(option)
                  ? "bg-[#F3F4F6]"
                  : "hover:bg-[#f5f5f5]",
                `w-full text-start px-5 py-2`
              )}
              key={option[props.valueField!]}
              onClick={() => methods.addItem(option)}
            >
              {option[props.labelField!]}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <label htmlFor="select" className="flex justify-between">
        <span className="mb-2 text-black">{label}</span>
        {required && <span className="text-[#BA0C2F] font-medium">*</span>}
      </label>
      <Select
        // disabled={disable}
        name="select"
        values={value}
        placeholder={placeholder}
        onChange={onChange}
        options={list}
        multi={multi}
        labelField="Title"
        valueField="ID"
        dropdownPosition="bottom"
        direction="ltr"
        required={required}
        color="#F3F4F6"
        addPlaceholder="выберите элементы"
        dropdownRenderer={(data) => dropdownRenderer(data)}
        dropdownGap={10}
        loading={loading}
        loadingRenderer={() => <Loading />}
        className={twMerge(
          "mt-2.5 hover:border hover:border-[#2563eb] ",
          disable ? "bg-gray3 !cursor-not-allowed !pointer-events-none" : "",
          className
        )}
        dropdownHandleRenderer={({ state }) => (
          <FlexCol>
            <button type="button" className="relative z-50">
              {state.dropdown ? (
                <ChevronUpIcon width={16} height={16} />
              ) : (
                <ChevronDownIcon width={16} height={16} />
              )}
            </button>
          </FlexCol>
        )}
        contentRenderer={({ state, props, methods }) => (
          <FlexRow
            className={twMerge(
              state.dropdown ? "border-[#2563eb]" : "",
              "text-[#9CA3AF] flex-wrap gap-y-2.5"
            )}
          >
            {state.values.length > 0
              ? state.values.map(
                  (item) =>
                    props.optionRenderer &&
                    props.optionRenderer({ item, state, props, methods })
                )
              : props.placeholder}
          </FlexRow>
        )}
        optionRenderer={({ item, props, methods }) => (
          <FlexCol
            className={twMerge(
              multi ? "bg-[#F3F4F6] px-2.5 rounded-[4px]" : "",
              "text-black py-1 flex-nowrap mr-2.5 relative z-50"
            )}
          >
            <FlexRow className="items-center justify-between gap-x-2.5">
              <span className="flex flex-col text-black">{item.Title}</span>

              {multi && (
                <button
                  onClick={(e) => methods.removeItem(e, item, false)}
                  className="flex flex-col translate-y-[.5px]"
                >
                  <XMarkIcon width={24} height={24} color="#9CA3AF" />
                </button>
              )}
            </FlexRow>
          </FlexCol>
        )}
      />
    </>
  );
}
